/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { noop, defaultTo } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Modal } from '@blend/enterprise-components';
import { ActionButton, Text, BrandColors, Header } from '@blend/enterprise-components';
import { ModalContent } from 'blb-components/ModalContent';

const styles = StyleSheet.create({
  center: {
    textAlign: 'center',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  indent: {
    paddingLeft: 16,
  },
  mainTextBody: {
    border: `1px solid ${BrandColors.CLOUD_50}`,
    borderRadius: 4,
    height: 440,
    marginBottom: 16,
    marginTop: 16,
    overflow: 'auto',
    padding: 16,
  },
});

export namespace TermsAndConditionsModal {
  export interface Props {
    btnLabel?: string;
    handleClose: () => void;
    isOpen: boolean;
  }
}

export const TermsAndConditionsModal: React.FC<TermsAndConditionsModal.Props> = ({ isOpen, handleClose, btnLabel }) => {
  const onAccept = () => {
    handleClose();
  };
  return (
    <Modal contentLabel="Terms and Conditions" handleClose={noop} isOpen={isOpen}>
      <ModalContent headerText="Terms and Conditions" id="it-TermsAndConditionsModal">
        <Text>
          <div className={css(styles.mainTextBody)}>
            <Text weight="semibold">Blend’s Terms of Use (Platform)</Text>
            <br />
            <br />
            <Text weight="semibold">EFFECTIVE DATE: June 25, 2020</Text>
            <br />
            <br />
            <Text>
              By clicking “I agree”, you hereby acknowledge and agree to the terms of this Services Agreement ("
              <Text weight="bold">Agreement</Text>"), effective as of the date you clicked “I Agree” (the “Effective
              Date”). This Agreement shall be by and between Blend Labs, Inc., a Delaware corporation with its principal
              place of business at 415 Kearny Street, San Francisco, CA 94108 (“<Text weight="bold">Company</Text>”) and
              you and the entity or agency that you are providing your products or services in connection with
              (collectively, "<Text weight="bold">Provider</Text>"). By clicking “I Agree”, you hereby represent and
              warrant that you have the authority to enter into this Agreement on behalf of yourself and the entity or
              agency that you are providing your products or services in connection with. Company and Provider shall
              each be referred to as a “<Text weight="bold">Party</Text>” in this agreement, and together shall be
              referred to as “<Text weight="bold">Parties</Text>.”
            </Text>
            <br />
            <br />
            <Header size={5}>DEFINITIONS</Header>
            <br />
            <Text weight="bold">“Affiliate”</Text>
            {` `}
            means any person or entity Controlling, Controlled by, or under common Control with that Party.
            <br />
            <br />
            <Text weight="bold">“Company Materials”</Text>
            {` `}
            means the Company (i) any application programming interfaces made available to Provider by Company and (ii)
            any related technical specifications or documentation relating to such application programing interfaces.
            <br />
            <br />
            <Text weight="bold">“Control,” “Controlling,”</Text> or <Text weight="bold">“Controlled”</Text>
            {` `}
            means when used with respect to a specified person or entity, more than 50% of the voting power of the
            shares (or other securities or rights) entitled to vote for the election of directors or other governing
            authority, as of the date of this Agreement or hereafter during the Term of this Agreement; provided that
            such person or entity shall be considered an Affiliate only for the time during which control exists.
            <br />
            <br />
            <Text weight="bold">“Consumer Information”</Text>
            {` `}
            means any non-public, personally-identifiable financial information collected about an End User.
            <br />
            <br />
            <Text weight="bold">“End Users”</Text>
            {` `}
            means consumers or customers, as those terms are defined by 16 CFR § 313.3, or potential customers of Lender
            Customer that access the Services.
            <br />
            <br />
            <Text weight="bold">“Confidential Information”</Text>
            {` `}
            means any and all non-public, confidential or proprietary information, disclosed by a Party (each a “
            <Text weight="bold">Discloser</Text>”) to the other Party (each a “Recipient”) irrespective of form, source
            and method of communication, provided it is conspicuously marked to identify its confidential or proprietary
            nature, or is reasonably understood to be confidential given the nature or circumstances surrounding its
            disclosure. Confidential Information includes without limitation, non-public information relating to
            Discloser's product and product development strategy and activity, corporate assessments and strategic
            plans, customer lists, financial information (past, current, and future), accounting information, hardware,
            software, systems, processes, data, samples, formulae, graphs, research and development, techniques,
            customer requirements, price lists, market studies, business plans, sales and marketing plans, marketing
            strategies, discoveries, regulatory affairs, employees, suppliers, unpatented inventions, ideas, methods and
            discoveries, trade secrets, know-how, designs, specifications, documentation, components, source code, and
            object code.
            <br />
            <br />
            <Text weight="bold">“Intellectual Property”</Text>
            {` `}
            means all intellectual property, including, without limitation, inventions, patents, patent applications,
            copyrights, trademarks, trademark applications, service marks, service mark applications, trade names,
            domain names, trade secrets, mask works, know-how, moral rights, licenses, and tangible embodiments of the
            foregoing and any other intangible proprietary or property rights, registered or not, under statute and/or
            common law.
            <br />
            <br />
            <Text weight="bold">“Lender Customers”</Text>
            {` `}
            means lenders customers of Company.
            <br />
            <br />
            <Text weight="bold">“Permitted Users”</Text>
            {` `}
            means Provider’s employees, third-party consultants, or contractors authorized by Company to use the
            Software.
            <br />
            <br />
            <Text weight="bold">“Provider Data”</Text>
            {` `}
            means the electronic data stored in or uploaded to the Services by or on behalf of Provider, Permitted Users
            or End Users including, without limitation, Consumer Information.
            <br />
            <br />
            <Text weight="bold">“Provider Services”</Text>
            {` `}
            means the title services provided by Provider to Lender Customers and End Users.
            <br />
            <br />
            <Text weight="bold">“Regulator”</Text>
            {` `}
            means a governmental authority or a non-governmental entity that is responsible for monitoring or overseeing
            the business practices or other activities of either of the respective Parties or any of their Affiliates,
            including, as applicable, Office of the Comptroller of the Currency, Consumer Financial Protection Bureau,
            Federal Deposit Insurance Corporation, National Credit Union Administration, or any other bodies or
            successors thereto that regulate banks, consumer finance companies, mortgage companies, and/or other
            financial services service providers.
            <br />
            <br />
            <Text weight="bold">“Services”</Text>
            {` `}
            means Company’s provisioning of access to Provider of the Software, subject to the terms of this Agreement,
            allowing the sharing of information between Provider, Lender Customers and End Users.
            <br />
            <br />
            <Text weight="bold">“Software”</Text>
            {` `}
            means Company's proprietary software hosted by Company and provided to Provider in connection with the
            Services subject to and in accordance with the terms of this Agreement. “Software” includes, without
            limitation, any updates, improvements, modifications, derivative works, patches and upgrades that Company
            develops or provides to or otherwise makes available to Provider in connection with this Agreement.
            <br />
            <br />
            <Header size={5}>SERVICES AND INTEGRATION</Header>
            <br />
            <Text underline weight="bold">
              Services.
            </Text>
            {` `}
            During the Term, Company will use commercially reasonable efforts to provide the Services in accordance with
            the terms of this Agreement. Company will not be liable for any failure to perform Services that is caused
            by Provider’s delay in or failure to provide Provider Data. Company may, in its reasonable discretion,
            limit, suspend or terminate access to the Services and Company Materials if and to the extent that, in
            Company’s reasonable determination, continued access represents a threat to data security, confidentiality
            obligations or potential violation of applicable law, Company’s agreements with applicable Lender Customers,
            or this Agreement.
            <br />
            <br />
            <Text underline weight="bold">
              Data Transmission.
            </Text>
            {` `}
            To the extent a Provider transmits Provider Data outside Company’s systems, Provider represents and warrants
            that Company is not responsible for the privacy, security or integrity of such data. Provider shall also
            maintain and handle all Provider Data in accordance with privacy and security measures reasonably adequate
            to preserve its confidentiality and security and all applicable privacy laws and regulations.
            Non-Exclusivity; Right to Independently Develop. Nothing in this Agreement shall be deemed to create an
            exclusive relationship between the Parties, and except as otherwise agreed upon by the Parties, Company may
            enter into agreements with other providers who provide services and products similar to the services
            provided by Provider (the “<Text weight="bold">Provider Services</Text>”). In addition and subject to not
            using either Party’s Confidential Information (as defined in Section 9) each Party shall be free to use its
            general knowledge, skills and experience within the scope of its business that are used or developed in the
            course of undertaking this Agreement by such Party, and each Party shall remain free to provide products and
            services to any client or prospective client, and to enter into an alliance, teaming or other arrangements
            with any third party (including competitors of the other party), so long as the terms of this Agreement are
            not violated.
            <br />
            <br />
            <Header size={5}>USE OF SERVICES</Header>
            <br />
            <Text underline weight="bold">
              Service Access.
            </Text>
            {` `}
            During the Term, and subject to compliance with the terms and conditions of this Agreement, Company hereby
            grants to Provider a limited, non-exclusive, non-sublicensable (except as expressly permitted hereunder)
            right and license to access and use Services for Permitted Users and End Users only; provided, that access
            to and use of the Services may be made available to End Users conditioned on each such End User agreeing to
            be bound, for Company’s benefit, to Company’s then-current terms of use and privacy policy, published on
            Company’s website.
            <br />
            <br />
            <Text underline weight="bold">
              Provider Data.
            </Text>
            {` `}
            Provider grants Company (including its Affiliates) a world-wide, non-exclusive, royalty-free license during
            the Term to use, reproduce, electronically distribute, and display the Provider Data solely to provide the
            Services. Notwithstanding anything to the contrary in this Agreement, subject to applicable laws, (a) during
            the Term, Company (including its Affiliates) may use Provider Data solely for internal business purposes in
            connection with the development, diagnostic, and corrective purposes and (b) during and following the Term,
            Company (including its Affiliates) may use Provider Data solely in a de-identified form for its business
            purposes.
            <br />
            <br />
            <Text underline weight="bold">
              Restrictions.
            </Text>
            {` `}
            Provider will not and will not knowingly permit any third party to (a) gain or attempt to gain unauthorized
            access to the Services, or any element thereof, or circumvent or otherwise interfere with any authentication
            or security measures of the Services; (b) interfere with or disrupt the integrity or performance of the
            Services; (c) transmit materials containing software viruses or other harmful or deleterious computer code,
            files, scripts, agents or program through the Services; (d) decompile, disassemble, reverse engineer or
            attempt to discover any source code or underlying ideas or algorithms of the Services (except to the extent
            that applicable law expressly prohibits such a reverse engineering restriction); (e) provide, lease, lend,
            use for timesharing or service bureau purposes, or for any purpose other than its own use for the benefit of
            its End Users and customers; (f) list or otherwise display or copy any code of the Services; (g) copy the
            Services (or component thereof), develop any improvement, modification or derivative work thereof, or
            include any portion thereof in any other service, equipment or item; (h) allow the transfer, transmission
            (including, without limitation, making available online, electronically transmitting, or otherwise
            communicating to the public), export, or re-export of the Services (or any portion thereof) or any technical
            data; (i) perform benchmark tests on the Services without the prior written consent of Company (any results
            of such permitted benchmark testing shall be deemed Company Confidential Information); (j) use, evaluate or
            view the Services for the purpose of designing, modifying or otherwise creating any environment, program or
            infrastructure (or any portion thereof), which performs functions similar to the functions performed by the
            Services; (k) use the Services in a manner that is harmful, fraudulent, deceptive, threatening, harassing,
            defamatory, obscene, or otherwise objectionable; (l) impersonate Company or its employees when using the
            Services; (m) use the Services for purposes of advertisement, sales promotions, or spam mail; (n) use the
            Services in a manner that mispresents Provider’s relationship with Company or is otherwise mislead; or (o)
            use the Service o store or transmit infringing, libelous, or otherwise unlawful or tortious material, or to
            store or transmit material in violation of third-party privacy rights.
            <br />
            <br />
            <Text underline weight="bold">
              Intellectual Property Rights of Company.
            </Text>
            {` `}
            Except as expressly set forth herein, Company (and its licensors, where applicable) shall own (i) the
            Services and Software and all improvements, enhancements or modifications made thereto, (ii) any and all
            work product or other materials, including software, inventions or other technology developed in connection
            with the Support Services or Professional Services; and (iii) all Intellectual Property rights related to
            any of the foregoing (the “<Text weight="bold">Company IP</Text>”). Provider acknowledges and agrees that
            any suggestions, ideas, enhancement requests, recommendations, or other feedback provided by Provider or any
            third party relating to the Services (“<Text weight="bold">Feedback</Text>”) shall be owned by Company and
            to the extent Provider owns any right, title, or interest in such Feedback, Provider hereby assigns and
            agrees to assign to Company all such right, title and interest in the Feedback. Additionally, Provider
            acknowledges and agrees that through its use of the Services Company may allow Lender Customer and End Users
            to rate the Provider Services and Company may distribute these ratings and user feedback. This Agreement is
            not a sale and does not convey to Provider any rights of ownership in or related to the Services, Software
            or any Intellectual Property rights therein or related thereto.
            <br />
            <br />
            <Text underline weight="bold">
              Provider Responsibilities.
            </Text>
            {` `}
            Provider shall be responsible for obtaining and maintaining any equipment and ancillary services needed to
            connect to, access or otherwise use the Services, including, without limitation, modems, hardware, servers,
            software, operating systems, networking, web servers, long distance and local telephone services
            (collectively, “<Text weight="bold">Equipment”</Text>).
            <br />
            <br />
            Provider is responsible for maintaining the confidentiality of all account information as well as any
            activities under its account (including under the account of any Permitted User). Provider shall be
            responsible for the security of its and its Permitted Users’ login credentials, passwords and security of
            any equipment that connects to the Software (including use of multi-factor authentication). Provider will
            also be responsible for establishing password protection, access controls or other procedures to verify that
            only designated Permitted Users have access to any administrative functions of the Services and personnel
            termination procedures. Provider agrees to keep all account information up to date and to (a) notify Company
            of any unauthorized use of any accounts or any other breach of security, and (b) promptly terminate the
            respective account upon a termination of employment of any Permitted User. Provider shall be responsible for
            the operation and maintenance of the Provider Services. Provider will cooperate with Company in connection
            with the performance of this Agreement by making available personnel and information as may be reasonably
            required and taking such actions as Company may reasonably request.
            <br />
            <br />
            <Text weight="bold">Provider Service Commitment.</Text>
            {` `}
            Provider acknowledges and agrees that Provider shall be responsible for ensuring that the Provider Services,
            together with any related support services are of high-quality and consistent with the industry standards.
            Provider acknowledges that Company may route support questions related to the Provider Services to Provider,
            and in such an event, Provider shall address such services requests as promptly as practicable.
            <br />
            <br />
            <Header size={5}>TERM AND TERMINATION</Header>
            <br />
            <Text underline weight="bold">
              Term.
            </Text>
            The term of this Agreement shall begin upon the Effective Date and continue until terminated in accordance
            with the terms of this Agreement (the “Term”).
            <br />
            <br />
            <Text underline weight="bold">
              Termination
            </Text>
            <br />
            <div className={css(styles.indent)}>
              <Text weight="bold">(i) Termination for Cause.</Text>
              {` `}
              In addition to any other remedies it may have, either Party may terminate this Agreement upon thirty (30)
              days written notice if the other Party materially breaches any of the terms or conditions of this
              Agreement, and fails to cure within thirty (30) days (or ten (10) days in case of non-payment) of written
              notice specifying the breach.
            </div>
            <br />
            <div className={css(styles.indent)}>
              <Text weight="bold">(ii) Termination for Insolvency.</Text>
              {` `}
              Either Party may terminate this Agreement, without any opportunity to cure by providing written notice to
              the other Party, in the event of (a) the institution by or against, the other Party of insolvency,
              receivership or bankruptcy proceedings; (b) the other Party's general assignment for the benefit of
              creditors; or (c) the other Party's dissolution or cessation of business operations
            </div>
            <br />
            <div className={css(styles.indent)}>
              <Text weight="bold">(iii) Termination for Convenience.</Text>
              {` `}
              Company may terminate this Agreement for any or no reason at any time.
            </div>
            <br />
            <br />
            <div className={css(styles.indent)}>
              <Text weight="bold">(iv) Company Termination.</Text>
              {` `}
              Company may terminate this Agreement immediately upon notice to Provider in the event that: (i) Company
              has received a material number of complaints regarding the Provider Services from Lender Customers or End
              Users, and Provider has not or is unable to correct or otherwise cure such complaints promptly following
              Company’s notice of such complaints, (ii) Company becomes aware of any violation by Provider of any
              applicable or applicable laws or regulations; or (iii) otherwise if the continued availability or
              provision of Provider Services constitutes a significant legal, compliance or data security risk to
              Company or Lender Customers.
            </div>
            <br />
            <div className={css(styles.indent)}>
              <Text weight="bold">(v) Effect of Termination.</Text>
              {` `}
              Upon any termination or expiration of this Agreement (a) each Party shall return or destroy, at the
              disclosing Party’s direction, any Confidential Information of the other Party or, with respect to
              Provider, any Company Materials and Provider shall immediately cease confidential and proprietary
              information, or other property, of the other party to such party; (b) the licenses or other rights granted
              hereunder shall terminate and Provider shall immediately cease using the Services; and (c) Company may
              terminate your account and access to the Services and sever any connections between Provider and any
              Lender Customers or End Users. In the event of any termination of this Agreement (other than with respect
              to Provider’s termination for cause pursuant to Section 7.1), Provider shall pay any outstanding fees owed
              (if applicable) to Provider at the time of such termination, which shall be paid within five (5) business
              days of termination. The provisions of this Agreement relating to intellectual property ownership,
              disclaimers of warranties, indemnities, limitations of liability and confidentiality, together with such
              other provisions that by their nature should reasonably be intended to survive, shall survive any
              termination or expiration of this Agreement.
            </div>
            <br />
            <Text underline weight="bold">
              Optional Transition Period.
            </Text>
            {` `}
            Notwithstanding anything to the contrary in this Agreement, in the event of a termination of this Agreement,
            at Company’s election, Provider shall continue to provide the Provider Services through the Services solely
            to the extent necessary to wind-down or transition any applicable title orders in progress through the
            Services.
            <br />
            <br />
            <Text underline weight="bold">
              Survival.
            </Text>
            {` `}
            All sections of this Agreement which by their nature should survive termination will survive termination,
            including, without limitation, with respect to accrued rights to payment, auditing, confidentiality
            obligations, warranty disclaimers and limitations of liability.
            <br />
            <br />
            <Header size={5}>FEES AND AUDIT RIGHTS</Header>
            <br />
            <Text underline weight="bold">
              Fees.
            </Text>
            {` `}
            Provider acknowledges and agrees that there may be charges associated with Provider’s use of certain
            features in connection with the Services and that such fees and features may be subject to accompanying
            terms and conditions.
            <br />
            <br />
            <Text underline weight="bold">
              Company Audit Rights.
            </Text>
            {` `}
            During the term of this Agreement and for three (3) years thereafter, Provider agrees to keep all usual and
            proper records and books of account and all usual and proper entries relating to compliance with the terms
            of this Agreement. With reasonable written notice, Company, at its expense, may cause an audit and/or
            inspection to be made of the applicable Provider records and facilities during normal business hours in
            order to verify Provider’s compliance with the terms of this Agreement. Any such audit shall be conducted by
            an auditor or inspector selected by Company. Such auditor or inspector shall provide a summary of its
            findings regarding Provider’s compliance with the terms of this Agreement. Provider agrees to provide
            Blend’s designated audit or inspection team access to the relevant Provider records and facilities.
            <br />
            <br />
            <Header size={5}>DATA SECURITY</Header>
            <br />
            Company and Provider agree to have implemented a comprehensive written information security program that
            includes administrative, technical, and physical safeguards appropriate to the size, complexity and the
            nature and scope of the Services provided to Provider under this Agreement inclusive of the data security
            requirements set forth in <Text weight="bold">Exhibit A</Text> attached hereto (“
            <Text weight="bold">Security Requirements</Text>”). Company and Provider hereby acknowledge and agree to
            comply with the provisions thereof.
            <br />
            <br />
            <Header size={5}>CONFIDENTIALITY</Header>
            <br />
            <Text underline weight="bold">
              Non-Disclosure.
            </Text>
            {` `}
            Each party shall protect all Confidential Information of the other party with the same degree of care as it
            uses to avoid unauthorized use, disclosure, publication, or dissemination of its own confidential
            information of a similar nature, but no less than reasonable care. Neither party shall disclose Confidential
            Information of the other party to any third party or use it for any purpose other than is necessary to
            perform its obligations and exercise its rights under this Agreement. Each party will disclose the other
            party’s Confidential Information only to those employees and authorized subcontractors on a need-to-know
            basis—provided that each is bound by confidentiality obligations consistent with this Agreement. The parties
            agree that the foregoing shall not apply with respect to any information that the Recipient can document
            (a) is or becomes generally available to the public; (b) was in the Recipient’s possession or known by the
            Recipient prior to receipt from the Discloser; (c) was rightfully disclosed to it by a third party without
            any obligation of confidentiality; (d) was independently developed without use of any Confidential
            Information of the Discloser, or (e) is required to so under an order from a court, administrative agency,
            or government body, by subpoena or by law (provided that the Recipient provides reasonable prior written
            notice to the Discloser or by demand of auditors, examiners, or Regulators).
            <br />
            <br />
            <Text underline weight="bold">
              Mandatory Disclosure.
            </Text>
            {` `}
            Recipient may make disclosures of Confidential Information to the extent required by applicable law, rule,
            regulation, order or requirement of a court, administrative agency or other governmental body (a “
            <Text weight="bold">Disclosure Requirement</Text>”); provided, that Recipient shall (i) notify the Discloser
            as promptly as practicable of the Disclosure Requirement, (ii) at Discloser’s expense, use all commercially
            reasonable efforts to limit disclosure and obtain confidential treatment or a protective order, or cooperate
            with Discloser in obtaining such remedy and (iii) if necessary, provide its consent so that Discloser may
            participate in any proceeding related to the Disclosure Requirement.
            <br />
            <br />
            <Text underline weight="bold">
              Marketing.
            </Text>
            {` `}
            Each Party hereby consents to allow the other Party to reference them by name in any promotional or
            marketing material with the prior written approval of the other Party. Nothing herein requires either to
            reference the other in any promotional or marketing materials. Notwithstanding the foregoing, Provider
            agrees to allow Company to identify Provider as a customer, including any Provider trademark(s) in marketing
            materials such as websites, blogs and general marketing collateral, provided that Company complies with
            Provider’s reasonable guidelines for trademark usage if disclosed to Company.
            <br />
            <br />
            <Header size={5}>DISASTER RECOVERY AND BUSINESS CONTINUITY</Header>
            <br />
            Each Party will be responsible for satisfying the following minimum requirements for which Company will not
            charge Provider additional fees. Each Party agrees to (i) maintain a disaster-recovery program, a business
            continuity plan, and an incident response plan (“Programs”), and (ii) test its Programs on an annual basis.
            In the event that the testing results identify any compliance or other issues with respect to the Parties’
            Programs, each Party shall use commercially reasonable efforts to correct any such issue. Upon each Party’s
            request, each Party will provide a copy of its Program then in effect.
            <br />
            <br />
            <Header size={5}>REPRESENTATIONS AND WARRANTIES</Header>
            <br />
            <Text underline weight="bold">
              Provider Warranties.
            </Text>
            {` `}
            As of and from the date of this Agreement and continuing all times during the existence thereof, Provider
            represents and warrants to Company as follows: This Agreement has been duly authorized, executed, and
            delivered by Provider and constitutes a legal and binding agreement of Provider, enforceable in accordance
            with its terms. Entry into and performance under this Agreement is not now and will not be restricted by any
            article of incorporation, partnership agreement, article of organization, charter, bylaw, operating
            agreement, judgment, decree, statute, rule, regulation, contract, or agreement of any kind applicable to
            Provider. Provider has all necessary rights, licenses, consents or authorizations required for Company to
            access and use Provider Data pursuant to and in accordance with this Agreement. Provider will comply with
            all laws and regulations applicable to its use of the Services.
            <br />
            <br />
            <Text underline weight="bold">
              Company Warranties.
            </Text>
            {` `}
            As of the Effective Date of this Agreement and continuing all times during the existence thereof, Company
            represents and warrants to Provider as follows: This Agreement has been duly authorized, executed and
            delivered by Company and constitutes a legal and binding agreement of Company, enforceable in accordance
            with its terms. Entry into and performance under this Agreement is not now and will not be restricted by any
            article of incorporation, partnership agreement, article of organization, charter, bylaw, operating
            agreement, judgment, decree, statute, rule, regulation, contract, or agreement of any kind applicable to
            Company. Company will comply in all material respects with all laws and regulations applicable to its
            provision of the Services. Company will provide the Services in accordance with this Agreement in a timely
            and professional manner by individuals of suitable competency, training and skill, and the Services shall
            substantially conform in all material respects to the applicable specifications or documentation. Company
            shall use commercially reasonable efforts consistent with prevailing industry standards to maintain the
            Services in a manner which minimizes errors and interruptions in the Services. Services may be temporarily
            unavailable for scheduled maintenance or for unscheduled emergency maintenance, either by Company or by
            third-party Providers, or because of other causes beyond Company’s reasonable control. Where possible,
            Company shall use commercially reasonable efforts to provide advance notice in writing or by e-mail of any
            scheduled service disruption.
            <br />
            <br />
            <Text fontSize="sm">
              EXCEPT AS EXPRESSLY SET FORTH IN THIS AGREEMENT, THE SERVICES ARE PROVIDED “AS IS” AND COMPANY DISCLAIMS
              ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY
              AND FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. ALL DISCLAIMERS OF ANY KIND (INCLUDING IN THIS
              SECTION AND ELSEWHERE IN THE AGREMENT) ARE MADE ON BEHALF OF THE COMPANY AND ITS AFFILIATES AND THEIR
              RESPECTIVE SHAREHOLDERS, DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, REPRESENTATIVES, AND SERVICE PROVIDERS.
              COMPANY DOES NOT PROVIDE ANY TAX, ACCOUNTING, REGULATORY OR LEGAL ADVICE, AND NOTHING PROVIDED IN
              CONNECTION WITH THE COMPANY’S SERVICES CONSTITUTEs SUCH ADVICE. PROVIDER’S USE OF THE INFORMATION AND
              MATERIALS MADE AVAILABLE THROUGH THE SERVICES IS AT PROVIDER’S SOLE RISK. PROVIDER AGREES THAT IT MUST
              EVALUATE—AND BEARS—ALL RISK ASSOCIATED WITH THE USE OF THE INFORMATION, INCLUDING THE ACCURACY,
              COMPLETENESS, OR USEFULNESS OF ANY MATERIALS AVAILABLE THROUGH THE SERVICES. Company does not warrant that
              the Services will be error-free, nor does it make any warranty as to the results that may be obtained from
              use of the Services. PROVIDER SPECIFICALLY ACKNOWLEDGES THAT COMPANY DOES NOT ATTEMPT TO INDEPENDENTLY
              VERIFY THE COMPLETENESS, ACCURACY OR AUTHENTICITY OF THE INFORMATION, AND THAT THE INFORMATION REPORTED TO
              AND BY THE SERVCES MAY BE SUBJECT TO TRANSCRIPTION AND TRANSMISSION ERRORS. COMPANY DOES NOT ACCEPT ANY
              RESPONSIBILITY FOR ANY CONCLUSION REGARDING ANY PARCEL OF RESIDENTIAL PROPERTY, FOR ANY DISCLOSURES MADE
              OR FOR ANY TRANSACTION OR DECISION THAT MAY ARISE FROM THE USE OF ANY INFORMATION. PROVIDER IS SOLELY
              RESPONSIBLE FOR THE ACCURACY OR APPROPRIATENESS OF ANY DISCLOSURE MADE, CONCLUSION OBTAINED AND FOR ANY
              ACTION THAT MAY BE TAKEN AS A RESULT. Provider agrees that its purchases are not contingent on the
              delivery of any future functionality or features, or dependent on any oral or written public comments made
              by COMPANY regarding future functionality or features.
            </Text>
            <br />
            <br />
            <Header size={5}>MUTUAL INDEMNIFICATION</Header>
            <br />
            <Text underline weight="bold">
              By Company.
            </Text>
            {` `}
            Company agrees to (a) defend Provider against any demand, claim, action or suit by an unaffiliated third
            party (each, a “<Text weight="bold">Claim</Text>”) that (the Services infringe any U.S. patent or U.S.
            copyright or misappropriates any trade secret of such third party and (b) indemnify Provider for associated
            settlement amounts or damages, liabilities, costs and expenses (including reasonable attorneys’ fees finally
            awarded to such third party by a court of competent jurisdiction or agreed to as part of a monetary
            settlement arising out of such Claim (collectively, “<Text weight="bold">Losses”</Text>). Notwithstanding
            anything to the contrary herein, Company shall have no liability or obligation to Provider (a) with respect
            to any Claim related to any content not created by Company; or (b) with respect to Company’s indemnification
            obligation pursuant to Section 12.1(a)(i), for Claims based on portions or components of the Services (i)
            resulting in whole or in part from Provider specifications or configurations, (ii) that are modified after
            delivery by Company, (iii) combined with other commercially unforeseen products, processes or materials
            where the alleged infringement relates to such combination, (iv) where Provider continues allegedly
            infringing activity after being notified thereof or after being informed of modifications that would have
            avoided the alleged infringement, or (v) where Provider’s use is not strictly in accordance with this
            Agreement or any documentation related to the Software and Services provided to Provider.
            <br />
            <br />
            <Text underline weight="bold">
              By Provider.
            </Text>
            {` `}
            Provider agrees to (a) defend Company, its Affiliates, and each of their respective employees, directors,
            contractors, and licensors against any Claim arising out of or relating to (i) use of the Services by
            Provider or Permitted Users not in accordance with the terms of this Agreement, except to the extent such
            Claim is subject to Company's obligations set forth in the preceding paragraph, (ii) any third party that
            gains access to the Services as a result of Provider’s or Permitted User’s failure to take reasonable
            measures to prevent unauthorized access to the Services, (iii) Provider’s or Permitted User’s provision of
            Provider Data to Company; and (b) indemnify Company for associated Losses.
            <br />
            <br />
            <Text underline weight="bold">
              Procedure.
            </Text>
            {` `}
            The obligations set forth in this Section are conditioned upon the following: (a) the Party seeking
            indemnification ("<Text weight="bold">Indemnitee</Text>") must notify the indemnifying Party ("
            <Text weight="bold">Indemnitor</Text>") in writing, promptly after receipt of actual notice of any Claim;
            (b) Indemnitor shall have sole control and authority with respect to the defense, litigation, compromise or
            settlement of such Claim (except to the extent that any settlement involves material commitments,
            responsibilities, or obligations on the part of Indemnitee in which case such settlement shall require the
            prior written consent of Indemnitee, which consent shall not be unreasonably delayed, conditioned, or
            withheld); and (c) Indemnitee shall provide reasonable information, cooperation and assistance as required
            by Indemnitor (at Indemnitor's expense). Indemnitee reserves the right to participate at its own cost in any
            proceedings with counsel of its own choosing, provided, however, that Indemnitee shall at all times be
            subject to Indemnitor's sole control and authority with respect to defending, litigating or settling the
            Claim.
            <br />
            <br />
            <Header size={5}>LIMITATION OF LIABILITY</Header>
            <br />
            IN NO EVENT WILL EITHER PARTY BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL OR CONSEQUENTIAL
            DAMAGES ARISING OUT OF OR IN ANY WAY CONNECTED WITH THE USE OF THE SERVICES OR ANYTHING PROVIDED IN
            CONNECTION WITH THIS AGREEMENT, THE DELAY OR INABILITY TO USE THE SERVICES OR ANYTHING PROVIDED IN
            CONNECTION WITH THIS AGREEMENT OR OTHERWISE ARISING FROM THIS AGREEMENT, INCLUDING WITHOUT LIMITATION, LOSS
            OF REVENUE OR ANTICIPATED PROFITS OR LOST BUSINESS OR LOST SALES, WHETHER BASED IN CONTRACT, TORT (INCLUDING
            NEGLIGENCE), STRICT LIABILITY OR OTHERWISE, EVEN IF COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF DAMAGES.
            THE TOTAL LIABILITY OF COMPANY (INCLUDING AFFILIATES), WHETHER BASED IN CONTRACT, TORT (INCLUDING NEGLIGENCE
            OR STRICT LIABILITY), OR OTHERWISE, WILL NOT EXCEED, IN THE AGGREGATE, THE FEES PAID TO COMPANY BY PROVIDER
            FOR THE SERVICES IN THE TWELVE-MONTH PERIOD PRECEDING THE DATE THAT A CLAIM OR DEMAND IS FIRST ASSERTED. THE
            FOREGOING LIMITATIONS WILL APPLY NOTWITHSTANDING ANY FAILURE OF ESSENTIAL PURPOSE OF ANY LIMITED REMEDY.
            <br />
            <br />
            <Header size={5}>U.S. GOVERNMENT MATTERS</Header>
            <br />
            The Service and Software are subject to the trade laws and regulations of the United States and other
            countries, including the Export Administration Regulations (EAR, 15 CFR Part 730 et seq.) and the sanctions
            programs administered by the Office of Foreign Assets Control (OFAC, 31 CFR Part 500). Provider will not
            import, export, re-export, transfer or otherwise use the Software or Service in violation of these laws and
            regulations, including by engaging in any unauthorized dealing involving (i) a U.S. embargoed country
            (currently Cuba, Iran, North Korea, Sudan and Syria); (ii) a party included on any restricted person list,
            such as the OFAC Specially Designated Nationals List, or the Commerce Department’s Denied Persons List or
            Entity List; or (iii) the design, development, manufacture, or production of nuclear, missile, or chemical
            or biological weapons. By using the Software and Services, Provider represents and warrants that Provider is
            not located in any such country or on any such list. Provider will not engage in activity that would cause
            Company to be violation of these laws and regulations.
            <br />
            <br />
            <Header size={5}>MISCELLANEOUS</Header>
            <br />
            <Text underline weight="bold">
              Notices.
            </Text>
            {` `}
            All notices under this Agreement will be in writing and will be deemed to have been duly given when received
            (a) if personally delivered; (b) when receipt is electronically confirmed, if transmitted by facsimile or
            e-mail; (c) the day after it is sent, if sent for next day delivery by recognized overnight delivery
            service; or (d) upon receipt, if sent by certified or registered mail, return receipt requested. Governing
            Law and Jurisdiction. This Agreement shall be governed by the laws of the State of California without regard
            to its conflict of laws provisions. Any action or proceeding to enforce rights under this Agreement shall be
            brought exclusively in the courts of San Francisco County, California and each Party hereby consents to its
            exclusive jurisdiction and venue.
            <br />
            <br />
            <Text underline weight="bold">
              Status of the Parties.
            </Text>
            {` `}
            No agency, partnership, joint venture or employment is created as a result of this Agreement, and no one
            Party is authorized to act on behalf of the other Party or bind the other Party in any respect whatsoever.
            Company and Provider agree they are acting as independent contractors and under no circumstances will any of
            the employees of one Party be deemed the employee of the other for any purpose.
            <br />
            <br />
            <Text underline weight="bold">
              Entire Agreement.
            </Text>
            {` `}
            Both Parties agree that this Agreement—together with the Exhibits is the complete and exclusive statement of
            the mutual understanding of the Parties and supersedes and cancels all previous written and oral agreements,
            communications, and other understandings relating to the subject matter of this Agreement, and that all
            waivers and modifications must be in a writing signed by both Parties, except as otherwise provided herein.
            <br />
            <br />
            <Text underline weight="bold">
              Severability.
            </Text>
            {` `}
            In the event that any provision of this Agreement shall be determined to be illegal or unenforceable, that
            provision shall be limited or eliminated to the minimum extent necessary so that this Agreement shall
            otherwise remain in full force and effect and enforceable.
            <br />
            <br />
            <Text underline weight="bold">
              Waiver; Precedence.
            </Text>
            {` `}
            The failure of either Party to enforce its rights under this Agreement at any time for any period shall not
            be construed as a waiver of such rights. It is the intention of the Parties that this Agreement be
            controlling over additional or different terms of any addenda, even if accepted in writing by both Parties,
            and that waivers and amendments shall be effective only if made in writing and mutually agreed upon by the
            Parties.
            <br />
            <br />
            <Text underline weight="bold">
              Headings.
            </Text>
            {` `}
            Section headings are for convenience and are not to be considered a substantive part of this agreement.
            <br />
            <br />
            <Text underline weight="bold">
              Dispute Resolution.
            </Text>
            {` `}
            The Parties agree that any and all disputes, claims or controversies arising out of or relating to this
            Agreement shall be submitted to JAMS, or its successor, for mediation; and if the matter is not resolved
            through mediation, then it shall be submitted to JAMS, or its successor, for final and binding arbitration
            pursuant to this Section. The Parties will mutually agree, in cooperation with JAMS, with respect to the
            selection of one or more mediators and the scheduling and location of the mediation proceedings; provided
            the Parties acknowledge and agree to share the costs of mediation equally among the Parties. Either Party
            may initiate arbitration with respect to the matters submitted to mediation by filing a written demand for
            arbitration at any time following the initial mediation session or at any time following 45 days from the
            date of filing the written request for mediation, whichever occurs first (“Initiation Date”). Except with
            respect to any claim to a court of competent jurisdiction for preliminary or interim equitable relief, or to
            compel arbitration in accordance with this Section or as otherwise agreed upon by the Parties, neither Party
            shall initiate an arbitration or litigation related to this Agreement prior to the Initiation Date. For the
            avoidance of doubt, the Parties acknowledge and agree that offers, promises, conduct and statements, whether
            oral or written, made in the course of the mediation by any of the Parties, their agents, employees, experts
            and attorneys, and by the mediator or any JAMS employees, are confidential, privileged and inadmissible for
            any purpose, including impeachment, in any arbitration or other proceeding involving the Parties, provided
            that evidence that is otherwise admissible or discoverable shall not be rendered inadmissible or
            non-discoverable as a result of its use in the mediation.
            <br />
            <br />
            <Text underline weight="bold">
              Assignment.
            </Text>
            {` `}
            Neither Party may assign this Agreement or any of its right or obligations hereunder without the other
            Party’s prior written consent; provided that either Party may assign this Agreement, and its rights and
            obligations, without the other Party’s consent to any third party who succeeds to substantially all of its
            business or assets, whether by sale of assets, merger or otherwise. Subject to the foregoing, this Agreement
            shall bind and inure to the benefit of the Parties, their respective successors, and permitted assigns.
            <br />
            <br />
            <Text underline weight="bold">
              Force Majeure.
            </Text>
            {` `}
            Neither Party shall be liable for delays in, or failure to deliver due to acts of God, acts of the other
            Party, acts of civil or military authority, acts of public enemy, fires, strikes, labor disputes, flood,
            epidemics, war, riots, civil disturbances, insurrections, accidents, explosions, earthquakes, the elements,
            or any other causes beyond their reasonable control, provided that such default or delay could not have been
            prevented by reasonable precautions and could not reasonably be circumvented by the non-performing Party
            through the use of alternate sources, workaround plans, or other means (including with respect to Company by
            Company meeting its obligations for performing disaster recovery services as described in this Agreement).
            In the event of a force majeure event, the non-performing Party will be excused from further performance or
            observance of the obligation(s) so affected for as long as such circumstances prevail and such Party
            continues to use commercially reasonable efforts to recommence performance or observance whenever and to
            whatever extent possible without delay. Any Party so delayed in its performance will immediately notify the
            Party to whom performance is due by email (to be confirmed in writing within two (2) days of the inception
            of such delay) and describe at a reasonable level of detail the circumstances causing such delay.
            <br />
            <br />
            <Text underline weight="bold">
              Legal Fees.
            </Text>
            {` `}
            In any action or proceeding to enforce rights under this Agreement, the prevailing Party will be entitled to
            recover costs and attorneys’ fees in accordance with applicable laws.
            <br />
            <br />
            <div className={css(styles.center)}>
              <Text fontSize="lg" underline weight="bold">
                EXHIBIT A
              </Text>
              <br />
              <Text fontSize="sm" underline weight="bold">
                Security requirements
              </Text>
            </div>
            <Text weight="bold">A) Provider Obligations</Text>
            <br />
            <div className={css(styles.indent)}>1) Security Breach</div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                <div className={css(styles.indent)}>
                  (a) Provider agrees to provide Company with notice via email of any confirmed unauthorized access,
                  transmission, disclosure, misuse, alteration, or destruction of any Consumer Information (a “Security
                  Breach”) within twenty-four (24) hours following the confirmation thereof. The written notice may be
                  delayed if any law enforcement agency determines that such notice will impede a criminal
                  investigation.
                </div>
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>2) Attestation of Security Practices</div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                (a) Provider agrees to the following and will provide evidence of the same upon request, which may be
                requested no more than once (1) per year:
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                <div className={css(styles.indent)}>
                  (i) Provider has implemented a comprehensive written information security program that includes
                  administrative, technical, and physical safeguards appropriate to the size, complexity, and the nature
                  and scope of the Services provided under this Agreement. Such security program shall include, but not
                  be limited to, the following:{' '}
                </div>
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                <div className={css(styles.indent)}>
                  <div className={css(styles.indent)}>
                    (a) access controls on information systems, including use of multi-factor authentication, and
                    controls to authenticate and permit access only to authorized individuals;
                  </div>
                </div>
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                <div className={css(styles.indent)}>
                  <div className={css(styles.indent)}>
                    (b) encryption of electronic Consumer Information, including while in transit or in storage on
                    networks or systems to which unauthorized individuals may have access;
                  </div>
                </div>
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                <div className={css(styles.indent)}>
                  <div className={css(styles.indent)}>
                    (c) monitoring systems and procedures to detect and respond to actual and attempted attacks on or
                    intrusions into systems storing Consumer Information;
                  </div>
                </div>
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                <div className={css(styles.indent)}>
                  <div className={css(styles.indent)}>
                    (d) measures to protect against destruction, loss, or damage of Consumer Information due to
                    potential environmental hazards, such as fire and water damage or technological failures.
                  </div>
                </div>
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                (ii) Provider shall adhere, in all material respects, to the applicable sections of the American Land
                Title Association Best Practices (ALTA BP) standards or similarly applicable security standards in the
                provision of the Services.
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                (iii) Provider’s information security programs comply with applicable laws and statutes applicable to
                the Services regarding the protection of Consumer Information.
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                (iv) Provider shall conduct, at least annually, at its expense, an internal audit of its security
                program to identify reasonably foreseeable threats that could result in a Security Breach.
              </div>
            </div>
            <br />
            <div className={css(styles.indent)}>
              <div className={css(styles.indent)}>
                (v) Provider shall will conduct, or cause to have conducted, a comprehensive background investigation on
                any of its Personnel. Provider shall not permit any of its employees or agents to perform material
                aspects of the Services under this Agreement if any such employees or agents have failed to pass such
                background check.
              </div>
            </div>
            <br />
          </div>
        </Text>
        <div className={css(styles.footer)}>
          <ActionButton
            id="it-TermsAndConditions-accept"
            label={defaultTo(btnLabel, 'I agree')}
            onClick={onAccept}
            theme="primary"
          />
        </div>
      </ModalContent>
    </Modal>
  );
};
